import { BasketCard } from '../../components/BasketCard';
import { EmptyMessage } from '../../components/EmptyMessage';
import { PriceDisplay } from '../../components/PriceDisplay';
import { ViewToggle } from '../../components/ViewToggle';
import type { ViewToggleOption } from '../../components/ViewToggle';
import { ItineraryView } from '../ItineraryView';
import { CalendarIcon } from '../../icons/CalendarIcon';
import { CheckIcon } from '../../icons/CheckIcon';
import { ListIcon } from '../../icons/ListIcon';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  ConfirmationCheck,
  ConfirmationRow,
  DiscountAmount,
  ItemsList,
  SummaryLabel,
  SummaryLine,
  TitleRow,
  TitleText,
  TotalLabel,
  TotalLine,
  TotalRow,
  Wrapper,
} from './BasketContents.styles';
import type {
  BasketContentsLabels,
  BasketContentsProps,
  BasketContentsView,
} from './BasketContents.types';

const DEFAULT_LABELS: Required<BasketContentsLabels> = {
  title: 'Your basket',
  itemisedView: 'Itemised view',
  itineraryView: 'Itinerary view',
  addedConfirmation: 'Added to basket',
  subtotal: 'Subtotal',
  promotions: 'Promotions',
  vouchers: 'Vouchers',
  delivery: 'Delivery',
  total: 'Total',
  removeLabel: 'Remove',
  removing: 'Removing…',
  noImageLabel: 'No image',
  selectGuestLabel: 'Select guest',
};

function renderBody({
  showItinerary,
  guests,
  isEmpty,
  emptyMessage,
  items,
  locale,
  resolvedLabels,
  onRemoveItem,
  removingItemId,
  isUpdating,
}: {
  showItinerary: boolean;
  guests: BasketContentsProps['guests'];
  isEmpty: boolean;
  emptyMessage: BasketContentsProps['emptyMessage'];
  items: BasketContentsProps['items'];
  locale: BasketContentsProps['locale'];
  resolvedLabels: Required<BasketContentsLabels>;
  onRemoveItem: BasketContentsProps['onRemoveItem'];
  removingItemId: string | null;
  isUpdating: boolean;
}) {
  if (isEmpty && emptyMessage != null) {
    return <EmptyMessage>{emptyMessage}</EmptyMessage>;
  }
  if (showItinerary && guests) {
    return <ItineraryView guests={guests} selectGuestLabel={resolvedLabels.selectGuestLabel} />;
  }
  return (
    <ItemsList>
      {items.map((item) => (
        <BasketCard
          key={item.id}
          {...item}
          locale={locale}
          noImageLabel={resolvedLabels.noImageLabel}
          removeLabel={resolvedLabels.removeLabel}
          removingLabel={resolvedLabels.removing}
          removing={item.id === removingItemId}
          removeDisabled={isUpdating && item.id !== removingItemId}
          onRemove={onRemoveItem ? () => onRemoveItem(item.id) : undefined}
        />
      ))}
    </ItemsList>
  );
}

export function BasketContents({
  items,
  totalPrice,
  view = 'itemised',
  onViewChange,
  guests,
  showAddedConfirmation = false,
  onRemoveItem,
  removingItemId = null,
  isUpdating = false,
  heading,
  showTitle = true,
  emptyMessage,
  discountSlot,
  subtotal,
  promotionsDiscount,
  vouchersDiscount,
  deliveryCost,
  locale,
  labels,
  className,
}: BasketContentsProps) {
  const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
  const hasGuests = guests !== undefined && guests.length > 0;
  const isEmpty = items.length === 0;
  const canToggleView = Boolean(onViewChange) && !isEmpty && hasGuests;
  const showItinerary = view === 'itinerary' && hasGuests;

  const toggleOptions: ViewToggleOption<BasketContentsView>[] = [
    { value: 'itemised', label: resolvedLabels.itemisedView, Icon: ListIcon },
    { value: 'itinerary', label: resolvedLabels.itineraryView, Icon: CalendarIcon },
  ];

  return (
    <Wrapper className={className} data-testid="basket-contents">
      {(showTitle || heading || canToggleView) && (
        <TitleRow>
          {showTitle && <TitleText>{heading ?? resolvedLabels.title}</TitleText>}
          {canToggleView && onViewChange && (
            <ViewToggle<BasketContentsView>
              value={view}
              onChange={onViewChange}
              options={toggleOptions}
            />
          )}
        </TitleRow>
      )}

      {showAddedConfirmation && (
        <ConfirmationRow role="status">
          <ConfirmationCheck aria-hidden="true">
            <CheckIcon size={12} />
          </ConfirmationCheck>
          {resolvedLabels.addedConfirmation}
        </ConfirmationRow>
      )}

      {renderBody({
        showItinerary,
        guests,
        isEmpty,
        emptyMessage,
        items,
        locale,
        resolvedLabels,
        onRemoveItem,
        removingItemId,
        isUpdating,
      })}

      {!isEmpty && (
        <>
          {discountSlot}

          <TotalRow>
            {subtotal && (
              <SummaryLine>
                <SummaryLabel>{resolvedLabels.subtotal}</SummaryLabel>
                <PriceDisplay
                  amount={subtotal.amount}
                  currencyCode={subtotal.currencyCode}
                  locale={locale}
                  size="base"
                  weight="normal"
                />
              </SummaryLine>
            )}
            {promotionsDiscount && (
              <SummaryLine>
                <SummaryLabel>{resolvedLabels.promotions}</SummaryLabel>
                <DiscountAmount>
                  −
                  {formatCurrency(promotionsDiscount.amount, promotionsDiscount.currencyCode, {
                    locale,
                  })}
                </DiscountAmount>
              </SummaryLine>
            )}
            {vouchersDiscount && (
              <SummaryLine>
                <SummaryLabel>{resolvedLabels.vouchers}</SummaryLabel>
                <DiscountAmount>
                  −
                  {formatCurrency(vouchersDiscount.amount, vouchersDiscount.currencyCode, {
                    locale,
                  })}
                </DiscountAmount>
              </SummaryLine>
            )}
            {deliveryCost && deliveryCost.amount > 0 && (
              <SummaryLine>
                <SummaryLabel>{resolvedLabels.delivery}</SummaryLabel>
                <PriceDisplay
                  amount={deliveryCost.amount}
                  currencyCode={deliveryCost.currencyCode}
                  locale={locale}
                  size="base"
                  weight="normal"
                />
              </SummaryLine>
            )}
            <TotalLine>
              <TotalLabel>{resolvedLabels.total}</TotalLabel>
              <PriceDisplay
                amount={totalPrice.amount}
                currencyCode={totalPrice.currencyCode}
                locale={locale}
                size="base"
                weight="bold"
              />
            </TotalLine>
          </TotalRow>
        </>
      )}
    </Wrapper>
  );
}
